import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import api, { Region } from '../services/api';
import { AwsInstanceType, InstanceCatalog } from '../types/providers';
import { formatRegionLocation } from '../utils/regionLocation';

export const maxBotsForType = (config: Record<string, any>, item: AwsInstanceType) => {
  const fleet = config.container_fleet || {};
  return Math.max(0, Math.min(32, item.ipv4_per_interface - 1,
    Math.floor((item.vcpu - 1) / (fleet.bot_cpus ?? 1)),
    Math.floor((item.memory_mib - 2048) / (fleet.bot_memory_mib ?? 2048))));
};

export function useAwsCatalog(providerId?: string, regionCode?: string) {
  const [catalog, setCatalog] = useState<InstanceCatalog | null>(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    setCatalog(null); setError('');
    if (!providerId || !regionCode) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const receive = (value: InstanceCatalog) => {
      if (!active) return;
      setCatalog(value);
      if (value.status === 'failed') setError(value.error || 'AWS machine discovery failed.');
      if (value.status === 'pending') timer = setTimeout(() => {
        api.getInstanceCatalog(providerId, regionCode).then(receive).catch(fail);
      }, 1500);
    };
    const fail = (err: any) => {
      if (active) setError(err.response?.data?.detail || 'Cannot load AWS machine types. Check provider access and retry.');
    };
    api.requestInstanceCatalog(providerId, regionCode, revision > 0).then(receive).catch(fail);
    return () => { active = false; clearTimeout(timer); };
  }, [providerId, regionCode, revision]);
  return { items: catalog?.items || [], loading: !!providerId && !!regionCode && !error && catalog?.status !== 'completed',
    error, refresh: () => setRevision(value => value + 1) };
}

export function AwsTypePicker({ items, value, onChange, loading, disabled, docker, architecture }: {
  items: AwsInstanceType[]; value: string; onChange: (item: AwsInstanceType) => void;
  loading?: boolean; disabled?: boolean; docker?: boolean; architecture?: string;
}) {
  const choices = items.filter(item => !docker || item.architecture === architecture);
  return <Autocomplete options={choices} value={choices.find(item => item.type === value) || null}
    loading={loading} disabled={disabled || loading} disableClearable={!!value}
    getOptionLabel={item => `${item.type} · ${item.vcpu} vCPU · ${item.memory_mib / 1024} GiB · ${item.architecture}`}
    isOptionEqualToValue={(a, b) => a.type === b.type}
    onChange={(_, item) => { if (item) onChange(item); }}
    renderInput={params => <TextField {...params} label={docker ? 'Host instance type' : 'VM instance type'}
      helperText={loading ? 'Loading types offered by AWS…' : `Types offered in this location.${docker ? ' Filtered to the host image architecture.' : ''}`}
      placeholder={value || 'Select an instance type'} />} />;
}

export default function AwsComputeSettings({ providerId, config, onChange, regionTypes, onRegionTypesChange, onValidityChange, disabled }: {
  providerId?: string; config: Record<string, any>; onChange: (config: Record<string, any>) => void;
  regionTypes: Record<string, string>; onRegionTypesChange: (value: Record<string, string>) => void;
  onValidityChange?: (valid: boolean) => void; disabled?: boolean;
}) {
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionId, setRegionId] = useState('');
  const [regionError, setRegionError] = useState('');
  const docker = config.bot_runtime === 'docker';
  const preferredRegion = config.container_fleet?.region || config.default_region || config.region;
  useEffect(() => {
    let active = true;
    setRegions([]); setRegionId(''); setRegionError('');
    if (providerId) api.getProviderRegions(providerId).then(rows => {
      if (active) {
        setRegions(rows);
        setRegionId(rows.find(row => row.region_code === preferredRegion)?.id || rows.find(row => row.configured)?.id || rows[0]?.id || '');
      }
    }).catch(() => { if (active) setRegionError('Cannot load provider regions. Reopen this form to retry.'); });
    return () => { active = false; };
  }, [providerId, preferredRegion]);
  const region = regions.find(row => row.id === regionId);
  const { items, loading, error, refresh } = useAwsCatalog(providerId, region?.region_code);
  const compute = config.compute || {};
  const entries = compute.instance_types || [];
  const name = regionTypes[regionId] || region?.launch_config?.instance_type || compute.default_instance_type || '';
  const selected = items.find(item => item.type === name);
  const entry = entries.find((item: any) => item.type === name);
  const count = entry?.bots_per_host;
  const maximum = selected ? maxBotsForType(config, selected) : 0;
  const architecture = config.container_fleet?.artifact_arch === 'amd64' ? 'x86_64' : 'arm64';
  let validation = '';
  if (selected && docker && selected.architecture !== architecture) validation = `This type does not match the ${architecture} host image.`;
  else if (selected && docker && (!Number.isInteger(count) || count < 1 || count > maximum)) {
    validation = `${name} supports at most ${maximum} bots with the current CPU/RAM limits and host reserve. Choose a larger host or enter 1–${maximum}.`;
  } else if (!loading && region && !selected) validation = 'Select a machine type offered in this location.';
  useEffect(() => {
    onValidityChange?.(!regionError && (!providerId || (!loading && !error && !validation)));
  }, [onValidityChange, providerId, loading, error, validation, regionError]);
  const updateEntry = (item: AwsInstanceType, capacity?: number) => {
    const existing = entries.find((row: any) => row.type === item.type);
    const next = { ...existing, type: item.type, architecture: item.architecture,
      ...(docker ? { bots_per_host: capacity ?? existing?.bots_per_host ?? count ?? 5 } : {}) };
    onChange({ ...config, compute: { ...compute,
      default_instance_type: compute.default_instance_type || item.type,
      instance_types: existing ? entries.map((row: any) => row.type === item.type ? next : row) : [...entries, next] } });
  };
  return <Stack spacing={2} sx={{ mb: 3 }}>
    <Box><Chip color="info" variant="outlined" label={docker ? 'Docker · shared hosts' : 'VM · one bot per machine'} /></Box>
    <Typography variant="body2" color="text.secondary">{docker ?
      'Each host runs multiple bots. Capacity is saved per instance type and applies to new hosts; existing hosts keep their allocation.' :
      'Each bot gets its own virtual machine. Shared-host capacity does not apply.'}</Typography>
    {!providerId && <Alert severity="info">Save provider credentials and add a region, then select an AWS machine type here.</Alert>}
    {regionError && <Alert severity="error">{regionError}</Alert>}
    {providerId && !regionError && regions.length === 0 && <Alert severity="info">Add a region to select machines available there.</Alert>}
    {regions.length > 0 && <>
      <TextField select fullWidth label="Machine location" value={regionId} disabled={disabled}
        onChange={event => setRegionId(event.target.value)}>
        {regions.map(row => <MenuItem key={row.id} value={row.id}>{formatRegionLocation(row.region_code)}</MenuItem>)}
      </TextField>
      {loading && <LinearProgress aria-label="Loading AWS machine types" />}
      {error && <Alert severity="error">{error}</Alert>}
      <AwsTypePicker items={items} value={name} loading={loading} disabled={disabled} docker={docker} architecture={architecture}
        onChange={item => { updateEntry(item); onRegionTypesChange({ ...regionTypes, [regionId]: item.type }); }} />
      <Button onClick={refresh} disabled={loading || disabled} sx={{ alignSelf: 'flex-start' }}>Refresh AWS types</Button>
      {selected && <Typography variant="body2">{selected.vcpu} vCPU · {selected.memory_mib / 1024} GiB RAM · Architecture: {selected.architecture} (from AWS)</Typography>}
      {docker && <TextField label="Bots per host" type="number" value={count ?? ''} disabled={disabled || !selected}
        inputProps={{ min: 1, max: maximum || 32, step: 1 }} error={!!validation}
        helperText={validation || `Up to ${maximum} bots on this type with the current resource limits.`}
        onChange={event => { if (selected) updateEntry(selected, Number(event.target.value)); }} />}
      {!docker && validation && <Alert severity="error">{validation}</Alert>}
      {docker && <Alert severity="info">The host image, network and operator key come from the fleet launch template. Each bot has its own public IP.</Alert>}
    </>}
  </Stack>;
}
